import {
  Component,
  Input,
  OnChanges,
  SimpleChanges,
  Output,
  EventEmitter,
  ViewChild,
} from "@angular/core";
import { formatMessage } from "devextreme/localization";
import {
  CalculatorUsFieldItemsService,
  UsCalculatorFieldItemsQuery,
} from "../../../../../services/calculator-us-field-items.service";
import { UsCalculatorFieldItem } from "../../../../../model/calculator-us-remastered/usCalculatorFieldItem";
import { DxAccordionComponent, DxFormComponent } from "devextreme-angular";
import { CaseTypeSpecification } from "../../../../../model/calculator-us-remastered/caseTypeSpecification";
import { SummaryItem } from "../../../../../model/calculator-us-remastered/summaryItem";
import { SelectedOption } from "../option-mapping-select-box/option-mapping-select-box.component";
import { SpineStampingOption } from "./spine-stamping-select/spine-stamping-select.component";

@Component({
  selector: "app-calculator-casetype-specification",
  templateUrl: "./calculator-casetype-specification.component.html",
  styleUrls: ["./calculator-casetype-specification.component.scss"],
})
export class CalculatorCasetypeSpecificationComponent implements OnChanges {
  @Input() selectedPublisherId: string | undefined;
  @Input() selectedClassification: string | undefined;
  @Input() selectedInitialReprint: string;

  private _caseTypeSpecification: CaseTypeSpecification;
  @Input()
  set caseTypeSpecification(val: CaseTypeSpecification) {
    this._caseTypeSpecification = val;
  }
  get caseTypeSpecification(): CaseTypeSpecification {
    return this._caseTypeSpecification;
  }

  @Output() summaryUpdate = new EventEmitter<SummaryItem[]>();
  @Output() onValueChanged = new EventEmitter<CaseTypeSpecification>();

  @ViewChild("caseTypeForm") caseTypeForm: DxFormComponent;
  @ViewChild("accordion") accordion: DxAccordionComponent;

  // Selected values for default selection
  private _selectedCaseType: UsCalculatorFieldItem | null = null;
  get selectedCaseType() {
    return this._selectedCaseType;
  }

  set selectedCaseType(value: UsCalculatorFieldItem | null) {
    this._selectedCaseType = value;
    this.caseTypeSpecification.caseType = value?.keyValue || undefined;
    this.onValueChanged.emit(this.caseTypeSpecification);
  }

  private _selectedBoardMaterial: SelectedOption | undefined = undefined;
  get selectedBoardMaterial(): SelectedOption | undefined {
    return this._selectedBoardMaterial;
  }
  set selectedBoardMaterial(val: SelectedOption | undefined) {
    this._selectedBoardMaterial = val;
    this.caseTypeSpecification.boardMaterialOptionId =
      val?.optionId || undefined;
    this.onValueChanged.emit(this.caseTypeSpecification);
  }

  private _selectedCaseMaterial: SelectedOption | undefined = undefined;
  get selectedCaseMaterial() {
    return this._selectedCaseMaterial;
  }

  set selectedCaseMaterial(value: SelectedOption | undefined) {
    this._selectedCaseMaterial = value;
    this.caseTypeSpecification.caseMaterialOptionId =
      value?.optionId || undefined;

    // The case material contains a material, color and finishing.
    // There are only specific combinations of these three parameters allowed.
    // Each combination points to one option.
    // In the summary it should be three separate entries.
    // So this parameter gets a special treatment here.
    if (value && value.optionName) {
      const [material, color, finishing] = value.optionName
        .split("|")
        .map((s) => s.trim());
      this.selectedCaseMaterialDisplayValue = material;
      this.selectedCaseColorDisplayValue = color;
      this.selectedCaseFinishingDisplayValue = finishing;
    }

    this.onValueChanged.emit(this.caseTypeSpecification);
  }

  selectedCaseMaterialDisplayValue: string = "";
  selectedCaseColorDisplayValue: string = "";
  selectedCaseFinishingDisplayValue: string = "";

  private _spineStampingOptionId: string | undefined;
  get spineStampingOptionId() {
    return this._spineStampingOptionId;
  }
  set spineStampingOptionId(value: string | undefined) {
    this._spineStampingOptionId = value;
    this.caseTypeSpecification.spineStampingOptionId = value;
    this.onValueChanged.emit(this.caseTypeSpecification);
  }
  private _spineStampingValue: number | undefined = 1;
  get spineStampingValue() {
    return this._spineStampingValue;
  }
  set spineStampingValue(value: number | undefined) {
    this._spineStampingValue = value;
    this.caseTypeSpecification.spineStampingOptionValue = value;
    this.onValueChanged.emit(this.caseTypeSpecification);
  }

  private _selectedFoil: UsCalculatorFieldItem | null = null;
  get selectedFoil() {
    return this._selectedFoil;
  }

  set selectedFoil(value: UsCalculatorFieldItem | null) {
    this._selectedFoil = value;
    this.caseTypeSpecification.foil = value?.keyValue || undefined;
    this.onValueChanged.emit(this.caseTypeSpecification);
  }

  private _selectedFrontBackStamping: UsCalculatorFieldItem | null = null;
  get selectedFrontBackStamping() {
    return this._selectedFrontBackStamping;
  }

  set selectedFrontBackStamping(value: UsCalculatorFieldItem | null) {
    this._selectedFrontBackStamping = value;
    this.caseTypeSpecification.frontBackStamping = value?.keyValue || undefined;
    this.onValueChanged.emit(this.caseTypeSpecification);
  }

  private _selectedFrontBackPanelFoil: UsCalculatorFieldItem | null = null;
  get selectedFrontBackPanelFoil() {
    return this._selectedFrontBackPanelFoil;
  }

  set selectedFrontBackPanelFoil(value: UsCalculatorFieldItem | null) {
    this._selectedFrontBackPanelFoil = value;
    this.caseTypeSpecification.frontBackPanelFoil =
      value?.keyValue || undefined;
    this.onValueChanged.emit(this.caseTypeSpecification);
  }

  formatMessage = formatMessage;
  collapsed: any = true;

  caseTypeDataSource: UsCalculatorFieldItem[] = [];

  // Additional field item arrays for extended Case Type fields
  caseMaterialFieldItems: UsCalculatorFieldItem[] = [];
  boardMaterialFieldItems: UsCalculatorFieldItem[] = [];
  caseColorFieldItems: UsCalculatorFieldItem[] = [];
  embossingFieldItems: UsCalculatorFieldItem[] = [];
  // caseDepthFieldItems: UsCalculatorFieldItem[] = []; // REMOVED - not needed anymore
  spineStampingFieldItems: UsCalculatorFieldItem[] = [];
  foilFieldItems: UsCalculatorFieldItem[] = [];
  frontBackStampingFieldItems: UsCalculatorFieldItem[] = [];
  frontBackFoilFieldItems: UsCalculatorFieldItem[] = [];

  // Case Wrap specific field item arrays
  caseWrapFieldItems: UsCalculatorFieldItem[] = [];
  casePaperFieldItems: UsCalculatorFieldItem[] = [];
  finishingFieldItems: UsCalculatorFieldItem[] = [];
  specialEffectsFieldItems: UsCalculatorFieldItem[] = [];
  printOverFoilFieldItems: UsCalculatorFieldItem[] = [];

  // Special values for editable fields
  currentFoilNumber: string = "10";

  // Parent component data access for Dies field visibility
  get shouldShowDiesField(): boolean {
    // We need to access the parent component's selectedInitialReprint
    // For now, we'll show it always - this can be refined later with proper parent communication
    return true;
  }

  boardMaterialSummaryItems: SummaryItem[] = [];

  onAccordionClick(e) {
    // Prevent default scroll behavior when accordion expands/collapses
    const scrollPosition =
      window.pageYOffset || document.documentElement.scrollTop;

    this.collapsed = e.component.option("selectedIndex") == -1;

    // Restore scroll position after accordion state change
    setTimeout(() => {
      window.scrollTo(0, scrollPosition);
    }, 100);
  }

  constructor(
    private calculatorUsFieldItemsService: CalculatorUsFieldItemsService
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.selectedClassification) {
      this.resetFieldItems();
      this.loadFieldItems();
    }

    if (changes.selectedCaseType) {
      this.loadDependentFieldItems();
    }

    this.updateSummary();
  }

  private loadFieldItems(): void {
    if (!this.selectedPublisherId || !this.selectedClassification) {
      return;
    }

    this.calculatorUsFieldItemsService
      .getUsCalculatorFieldItems(<UsCalculatorFieldItemsQuery>{
        publisherId: this.selectedPublisherId,
        classification: this.calculatorUsFieldItemsService.getDbClassification(
          this.selectedClassification
        ),
        subcategory: "CASE_TYPE",
        inputName: "TYPE",
      })
      .subscribe({
        next: (items) => {
          // Map the case type items to the data source
          this.caseTypeDataSource = items;
          this._selectedCaseType = null;

          // Find default case type if available
          const defaultCaseType = items.find((item) => item.isDefault);
          if (defaultCaseType && !this._selectedCaseType) {
            this._selectedCaseType = defaultCaseType;
          }

          // Load dependent items based on default selection
          this.loadDependentFieldItems();
        },
        complete: () => {
          this.updateSummary();
        },
      });
  }

  loadDependentFieldItems(): void {
    if (
      this._selectedCaseType &&
      this.selectedPublisherId &&
      this.selectedClassification
    ) {
      this.calculatorUsFieldItemsService
        .getDependantFieldItems(
          this.selectedPublisherId,
          this.calculatorUsFieldItemsService.getDbClassification(
            this.selectedClassification
          ),
          "CASE_TYPE",
          "TYPE",
          this._selectedCaseType.keyValue || ""
        )
        .subscribe({
          next: (fieldItems) => {
            // Reset all data sources before mapping
            this.boardMaterialFieldItems = [];
            this.caseMaterialFieldItems = [];
            this.caseColorFieldItems = [];
            this.spineStampingFieldItems = [];
            this.foilFieldItems = [];
            this.frontBackStampingFieldItems = [];
            this.casePaperFieldItems = [];
            this.finishingFieldItems = [];
            this.specialEffectsFieldItems = [];
            this.embossingFieldItems = [];

            this.resetAllFieldSelections();

            // Map each field item to the correct data source
            fieldItems.forEach((item) => {
              switch (item.inputName) {
                case "SPINE_STAMPING":
                  // load the optionId from the spine stamping select_box
                  if (
                    item.inputType === "OPTION_SELECT" &&
                    this.matchesDependsOnSelectedCaseType(item)
                  ) {
                    this.spineStampingOptionId = item.keyValue;
                  }
                  break;
                case "FOIL":
                  this.foilFieldItems.push(item);
                  if (item.isDefault && !this._selectedFoil) {
                    this._selectedFoil = item;
                  }
                  break;
                case "FRONT_BACK_PANEL_STAMPING":
                  this.frontBackStampingFieldItems.push(item);
                  if (item.isDefault && !this._selectedFrontBackStamping) {
                    this._selectedFrontBackStamping = item;
                  }
                  break;

                default:
                  break;
              }
            });
          },
          error: (error) => {
            console.error("Error loading dependent field items:", error);
          },
          complete: () => {
            this.updateSummary();
          },
        });
    }
  }

  /**
   * Helper method to determine if the given field item matches the criteria based on the selected case type.
   */
  private matchesDependsOnSelectedCaseType(
    fieldItem: UsCalculatorFieldItem
  ): boolean {
    if (!fieldItem.dependsOnField) {
      return true;
    } else if (this.selectedCaseType?.inputName === fieldItem.dependsOnField) {
      return this.selectedCaseType?.keyValue === fieldItem.dependsOnValue;
    }
    return false;
  }

  onCaseTypeChanged($event: any) {
    this._selectedCaseType =
      this.caseTypeDataSource.find((item) => item.keyValue === $event.value) ||
      null;
    // Reset all field selections when Case Type changes
    this.resetAllFieldSelections();

    // Load Case Type-dependent field items with new defaults
    this.loadDependentFieldItems();
  }

  /**
   * Reset all field selections when Case Type changes
   */
  private resetAllFieldSelections(): void {
    // Reset standard fields (used for 1-Piece and 3-Piece)
    this._selectedBoardMaterial = undefined;
    this._selectedCaseMaterial = undefined;
    this._spineStampingOptionId = undefined;
    this._spineStampingValue = 1;
    this._selectedFoil = null;
    this._selectedFrontBackStamping = null;

    this.selectedCaseMaterialDisplayValue = "";
    this.selectedCaseColorDisplayValue = "";
    this.selectedCaseFinishingDisplayValue = "";
  }

  /**
   * Aktualisiert die Summary mit den aktuellen Case Type Werten
   */
  private updateSummary(): void {
    const summaryItems: SummaryItem[] = [...this.boardMaterialSummaryItems];

    // Case Type
    if (this.caseTypeDataSource.length > 0) {
      const defaultItem =
        this.caseTypeDataSource.find((item) => item.isDefault) || null;
      summaryItems.push(
        new SummaryItem(
          "caseType",
          "Case Type",
          this._selectedCaseType?.displayValue || "",
          defaultItem?.displayValue || "",
          "Case Type Specification",
          undefined
        )
      );
    }

    // Conditional fields based on Case Type
    const isCaseWrap =
      this._selectedCaseType?.keyValue?.toUpperCase() === "CASE_WRAP";

    if (isCaseWrap) {
      // Case Wrap specific fields
      this.addCaseWrapSummaryItems(summaryItems);
    } else {
      // 1-Piece and 3-Piece fields
      this.addStandardCaseTypeSummaryItems(summaryItems);
      this.addSpineStampingSummaryItems(summaryItems);
    }

    // Emit summary update
    this.summaryUpdate.emit(summaryItems);
  }

  /**
   * Add summary items for standard Case Types (1-Piece, 3-Piece)
   */
  private addStandardCaseTypeSummaryItems(summaryItems: SummaryItem[]): void {
    // Case Material (uses original Default Product values as reference)
    if (this.caseMaterialFieldItems.length > 0) {
      const defaultItem =
        this.caseMaterialFieldItems.find((item) => item.isDefault) || null;

      // special treatment of case material, see comment above
      const [defaultMaterial, defaultColor, defaultFinishing] =
        defaultItem?.displayValue
          ? defaultItem.displayValue.split("|").map((s) => s.trim())
          : ["", "", ""];

      summaryItems.push(
        new SummaryItem(
          "caseMaterial",
          "Case Material",
          this.selectedCaseMaterialDisplayValue || "",
          defaultMaterial,
          "Case Type Specification",
          undefined
        )
      );

      summaryItems.push(
        new SummaryItem(
          "caseColor",
          "Color",
          this.selectedCaseColorDisplayValue || "",
          defaultColor,
          "Case Type Specification",
          undefined
        )
      );

      summaryItems.push(
        new SummaryItem(
          "embossing",
          "Finishing",
          this.selectedCaseFinishingDisplayValue || "",
          defaultFinishing,
          "Case Type Specification",
          undefined
        )
      );
    }

    // Foil (uses original Default Product values as reference)
    if (this.foilFieldItems && this.foilFieldItems.length > 0) {
      const defaultItem =
        this.foilFieldItems.find((item) => item.isDefault) || null;
      summaryItems.push(
        new SummaryItem(
          "foil",
          "Foil",
          this._selectedFoil?.displayValue || "",
          defaultItem?.displayValue || "",
          "Case Type Specification",
          undefined
        )
      );
    }

    // Front and Back Panel Stamping (uses original Default Product values as reference)
    if (
      this.frontBackStampingFieldItems &&
      this.frontBackStampingFieldItems.length > 0 &&
      this._selectedFrontBackStamping
    ) {
      if (this._selectedFrontBackStamping.keyValue?.toUpperCase() !== "NONE") {
        const defaultItem =
          this.frontBackStampingFieldItems.find((item) => item.isDefault) ||
          null;
        summaryItems.push(
          new SummaryItem(
            "frontBackStamping",
            "Front and Back Panel Stamping",
            this._selectedFrontBackStamping.displayValue || "",
            defaultItem?.displayValue || "",
            "Case Type Specification",
            undefined
          )
        );
      } else {
        // Remove any existing frontBackStamping items from summaryItems if "NONE" is selected
        const index = summaryItems.findIndex(
          (item) => item.key === "frontBackStamping"
        );
        if (index > -1) {
          summaryItems.splice(index, 1);
        }
      }
    }
  }

  private addSpineStampingSummaryItems(summaryItems: SummaryItem[]): void {
    if (this.spineStampingOptionId !== undefined) {
      const defaultValue = `Stamping sqn (${this.spineStampingValue})`;
      let currentValue: string;
      switch (this.spineStampingValue) {
        case undefined:
          currentValue = "None";
          break;
        case 0:
          currentValue = "10 sqn included";
          break;
        default:
          currentValue = `Stamping sqn (${this.spineStampingValue})`;
          break;
      }

      summaryItems.push(
        new SummaryItem(
          "spineStamping",
          "Spine Stamping",
          currentValue,
          defaultValue,
          "Case Type Specification",
          undefined
        )
      );
    }
  }

  /**
   * Add summary items for Case Wrap Case Type
   */
  private addCaseWrapSummaryItems(summaryItems: SummaryItem[]): void {
    // tbd
  }

  caseMaterialChanged(value: SelectedOption | undefined) {
    this.selectedCaseMaterial = value;

    this.updateSummary();
  }

  // only required for the special summary update treatment for this field
  caseMaterialFieldItemsChanged(fieldItems: UsCalculatorFieldItem[]) {
    this.caseMaterialFieldItems = fieldItems;
  }

  // caseDepthChanged() method REMOVED - Case Depth field no longer exists

  // New event handler for Board Material
  boardMaterialChanged(value: SelectedOption | undefined) {
    this.selectedBoardMaterial = value;
  }

  setBoardMaterialSummaryItems($event: any): void {
    const summaryItems: SummaryItem[] = $event;
    this.boardMaterialSummaryItems = [...summaryItems];
    this.updateSummary();
  }

  spineStampingChanged(value: number | undefined) {
    this.spineStampingValue = value;
    this.updateSummary();
  }

  // Updated event handler for Foil (renamed from typeOfFoilChanged)
  foilChanged($event: any) {
    const selectedValue = $event.value;
    this._selectedFoil =
      this.foilFieldItems.find((item) => item.keyValue === selectedValue) ||
      null;
    this.updateSummary();
  }

  // Updated event handler for Front and Back Panel Stamping (renamed from frontStampingChanged)
  frontBackStampingChanged($event: any) {
    const selectedValue = $event.value;
    this._selectedFrontBackStamping =
      this.frontBackStampingFieldItems.find(
        (item) => item.keyValue === selectedValue
      ) || null;
    if (selectedValue && String(selectedValue).toUpperCase() != "NONE")
      this.updateSummary();
  }

  public resetFieldItems(): void {
    this.boardMaterialSummaryItems = [];

    this.selectedCaseType = null;
    this.selectedBoardMaterial = undefined;
    this.selectedCaseMaterial = undefined;
    this.spineStampingValue = 1;
    this.spineStampingOptionId = undefined;
    this.selectedFoil = null;
    this.selectedFrontBackStamping = null;
    this.selectedFrontBackPanelFoil = null;

    this.caseTypeDataSource = [];
    this.caseMaterialFieldItems = [];
    this.boardMaterialFieldItems = [];
    this.caseColorFieldItems = [];
    this.embossingFieldItems = [];
    this.spineStampingFieldItems = [];
    this.foilFieldItems = [];
    this.frontBackStampingFieldItems = [];
    this.frontBackFoilFieldItems = [];

    this.caseWrapFieldItems = [];
    this.casePaperFieldItems = [];
    this.finishingFieldItems = [];
    this.specialEffectsFieldItems = [];
    this.printOverFoilFieldItems = [];

    if (this.accordion) this.accordion.instance.collapseItem(0).then();
    this.summaryUpdate.emit([]);
  }
}
